/** @jsxImportSource preact */
import type { ComponentChildren } from "preact";
import { createPortal } from "preact/compat";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

import type {
  FeedbackDraft,
  FeedbackReason,
  FeedbackSnapshot,
} from "@go-novus/admin-api";

import { MerchantFeedbackNetworkError } from "../config";
import * as m from "../paraglide/messages.js";
import { CheckIcon, CopyIcon, ThumbIcon } from "./icons";
import { Drawer } from "./drawer/base-ui.mjs";
import { SR_ONLY } from "./sr-only";
import { useCopy } from "./use-copy";
import { useIsMobile } from "./use-is-mobile";

interface MessageActionsProps {
  /** 给了才画复制按钮。 */
  text?: string;
  delay: string;
  edge?: "start" | "end";
  snapshot?: FeedbackSnapshot;
  onLoadReasons?: () => Promise<FeedbackReason[]>;
  /** 给了才画点赞、点踩。店面不传。 */
  onSubmit?: (draft: FeedbackDraft) => Promise<FeedbackSnapshot>;
  onLikeError?: (message: string) => void;
  /** 已格式化的发送时刻。有赞踩时排在它们后面。 */
  sentAt?: { label: string; value: string };
}

const TIP_MS = 1400;
const POPOVER_GAP = 8;
const POPOVER_MARGIN = 8;

export function placeFeedbackPopover(
  anchor: { top: number; right: number; bottom: number; left: number },
  width: number,
  height: number,
  viewport: { width: number; height: number },
  edge: "start" | "end",
  boundary = {
    top: 0,
    right: viewport.width,
    bottom: viewport.height,
    left: 0,
  },
): {
  top: number | null;
  bottom: number | null;
  left: number;
  maxHeight: number;
} | null {
  const measurable = anchor.bottom > anchor.top || anchor.right > anchor.left;
  if (
    measurable &&
    (anchor.bottom <= boundary.top ||
      anchor.top >= boundary.bottom ||
      anchor.right <= boundary.left ||
      anchor.left >= boundary.right)
  ) {
    return null;
  }
  const spaceBelow =
    boundary.bottom - anchor.bottom - POPOVER_GAP - POPOVER_MARGIN;
  const spaceAbove = anchor.top - boundary.top - POPOVER_GAP - POPOVER_MARGIN;
  // 整框放得下就放下面。放不下才翻上去；两边都放不下，用空间更大的那一侧。
  const below =
    spaceBelow >= height || (spaceAbove < height && spaceBelow >= spaceAbove);
  const boxWidth = Math.min(
    width,
    boundary.right - boundary.left - POPOVER_MARGIN * 2,
  );
  let left = edge === "end" ? anchor.right - boxWidth : anchor.left;
  const minLeft = boundary.left + POPOVER_MARGIN;
  const maxLeft = boundary.right - boxWidth - POPOVER_MARGIN;
  left = Math.min(Math.max(minLeft, left), Math.max(minLeft, maxLeft));
  if (below) {
    const top = anchor.bottom + POPOVER_GAP;
    return {
      top,
      bottom: null,
      left,
      maxHeight: Math.max(0, boundary.bottom - POPOVER_MARGIN - top),
    };
  }
  const bottom = viewport.height - anchor.top + POPOVER_GAP;
  return {
    top: null,
    bottom,
    left,
    maxHeight: Math.max(
      0,
      anchor.top - boundary.top - POPOVER_GAP - POPOVER_MARGIN,
    ),
  };
}

function composedParent(element: Element): Element | null {
  if (element.parentElement !== null) return element.parentElement;
  const root = element.getRootNode();
  return root instanceof ShadowRoot ? root.host : null;
}

function feedbackBoundary(anchor: Element) {
  let element = composedParent(anchor);
  while (element !== null) {
    const overflow = getComputedStyle(element).overflowY;
    if (overflow === "auto" || overflow === "scroll") {
      return element.getBoundingClientRect();
    }
    element = composedParent(element);
  }
  return {
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
    left: 0,
  };
}

function inFeedback(
  path: EventTarget[],
  root: HTMLElement | null,
  popover: HTMLElement | null,
) {
  return path.some((node) => node === root || node === popover);
}

const POPOVER_VARS = [
  "--gn-panel",
  "--gn-surface-solid",
  "--gn-field",
  "--gn-ink",
  "--gn-primary",
  "--gn-divider",
  "--gn-line",
  "--gn-action",
  "--gn-action-hover",
  "--gn-message-line",
  "--gn-message-bg",
  "--gn-message-fg",
  "--gn-danger",
  "--gn-action-accent",
  "--gn-action-accent-fg",
];

const FEEDBACK_POPOVER_STYLES = `
.gn-feedback-popover { position:absolute; z-index:30; visibility:hidden; box-sizing:border-box; width:max-content; max-width:calc(100vw - 16px); overflow:auto; margin:0; border:1px solid var(--gn-divider, rgba(127,127,127,.22)); border-radius:14px; background:var(--gn-surface-solid, var(--gn-panel, Canvas)); color:var(--gn-ink, CanvasText); box-shadow:0 12px 32px rgba(0,0,0,.24), 0 2px 8px rgba(0,0,0,.12); }
.gn-feedback-form, .gn-feedback-note { box-sizing:border-box; margin:0; padding:14px; border:0; border-radius:inherit; background:transparent; color:inherit; text-align:start; }
.gn-feedback-form { width:min(320px, calc(100vw - 16px)); }
.gn-feedback-note { width:max-content; max-width:min(320px, calc(100vw - 16px)); }
.gn-feedback-title { margin:0 0 4px; font-size:14px; font-weight:650; }
.gn-feedback-help, .gn-feedback-note { font-size:12px; line-height:1.4; }
.gn-feedback-help { color:var(--gn-action, currentColor); }
.gn-feedback-note p { margin:0; }
.gn-feedback-note p + p { margin-top:6px; }
.gn-feedback-meta { color:var(--gn-action); }
.gn-feedback-reason { display:flex; gap:8px; align-items:center; margin-top:2px; padding:5px 8px; border-radius:8px; font-size:13px; line-height:1.35; cursor:pointer; }
.gn-feedback-reason:hover { background:var(--gn-action-hover, rgba(127,127,127,.12)); }
.gn-feedback-reason input { margin:0; accent-color:var(--gn-primary, var(--gn-action-accent)); }
.gn-feedback-reasons-loading { display:grid; gap:2px; margin-top:2px; }
.gn-feedback-reason-skeleton { height:27px; border-radius:8px; background:color-mix(in srgb, var(--gn-action, currentColor) 12%, transparent); animation:gnFeedbackSkeleton 1s ease-in-out infinite alternate; }
.gn-feedback-details { box-sizing:border-box; width:100%; min-height:72px; margin-top:8px; padding:9px 10px; border:1px solid var(--gn-line, var(--gn-divider, rgba(127,127,127,.28))); border-radius:9px; outline:none; background:var(--gn-field, Canvas); color:var(--gn-ink, CanvasText); caret-color:var(--gn-primary, currentColor); font:inherit; font-size:var(--text-title, 13px); }
.gn-feedback-details::placeholder { color:var(--gn-action, GrayText); opacity:.85; }
.gn-feedback-details:focus { border-color:var(--gn-primary, var(--gn-action-accent)); box-shadow:0 0 0 2px color-mix(in srgb, var(--gn-primary, var(--gn-action-accent)) 20%, transparent); }
.gn-feedback-error { display:flex; align-items:center; gap:6px; margin:8px 0 0; color:var(--gn-danger, #d92d20); font-size:12px; }
.gn-feedback-retry { padding:0; border:0; background:transparent; color:inherit; font:inherit; font-weight:650; text-decoration:underline; cursor:pointer; }
.gn-feedback-buttons { display:flex; gap:8px; margin-top:12px; }
.gn-feedback-send, .gn-feedback-cancel { min-height:36px; padding:0 12px; border-radius:9px; font:inherit; font-size:13px; cursor:pointer; }
.gn-feedback-send { border:0; background:var(--gn-action-accent); color:var(--gn-action-accent-fg); }
.gn-feedback-send:disabled { opacity:.5; cursor:default; }
.gn-feedback-cancel { border:1px solid var(--gn-line, var(--gn-divider, rgba(127,127,127,.28))); background:var(--gn-field, transparent); color:inherit; }
.gn-feedback-cancel:hover { background:var(--gn-action-hover, rgba(127,127,127,.12)); }
.gn-feedback-sheet-viewport { position:fixed; z-index:980; inset:0 0 var(--drawer-keyboard-inset, 0px); display:flex; align-items:flex-end; justify-content:center; }
.gn-feedback-sheet-backdrop { position:absolute; inset:0; border:0; background:rgb(0 0 0 / 42%); animation:gnFeedbackBackdropIn .2s ease-out; }
.gn-feedback-sheet { position:relative; box-sizing:border-box; width:100%; max-height:calc(100% - max(16px, env(safe-area-inset-top, 0px))); overflow:auto; overscroll-behavior:contain; border:1px solid var(--gn-divider, rgba(127,127,127,.22)); border-bottom:0; border-radius:18px 18px 0 0; outline:none; background:var(--gn-surface-solid, var(--gn-panel, Canvas)); color:var(--gn-ink, CanvasText); box-shadow:0 -12px 36px rgba(0,0,0,.24); transform:translateY(var(--drawer-swipe-movement-y, 0px)); transition:transform .28s cubic-bezier(.32,.72,0,1); animation:gnFeedbackSheetIn .32s cubic-bezier(.32,.72,0,1); }
.gn-feedback-sheet[data-starting-style], .gn-feedback-sheet[data-ending-style] { transform:translateY(102%); }
.gn-feedback-sheet[data-swiping] { transition:none; }
.gn-feedback-sheet-handle { width:36px; height:4px; margin:6px auto 0; border-radius:999px; background:var(--gn-action, currentColor); opacity:.3; }
.gn-feedback-sheet .gn-feedback-form, .gn-feedback-sheet .gn-feedback-note { width:100%; max-width:none; padding:8px 16px calc(12px + max(0px, env(safe-area-inset-bottom, 0px) - var(--drawer-keyboard-inset, 0px))); }
.gn-feedback-sheet .gn-feedback-title { margin-bottom:2px; font-size:15px; line-height:1.2; }
.gn-feedback-sheet .gn-feedback-help { margin-bottom:4px; font-size:12px; line-height:1.3; }
.gn-feedback-sheet .gn-feedback-reason { min-height:34px; box-sizing:border-box; margin-top:0; padding:4px; font-size:13px; line-height:1.2; }
.gn-feedback-sheet .gn-feedback-reason input { width:16px; height:16px; }
.gn-feedback-sheet .gn-feedback-details { min-height:80px; margin-top:6px; font-size:16px; }
.gn-feedback-sheet .gn-feedback-buttons { position:sticky; bottom:0; margin-top:8px; padding-top:4px; background:var(--gn-surface-solid, var(--gn-panel, Canvas)); }
.gn-feedback-sheet .gn-feedback-send, .gn-feedback-sheet .gn-feedback-cancel { min-height:40px; flex:1; font-size:13px; }
@keyframes gnFeedbackBackdropIn { from { opacity:0; } }
@keyframes gnFeedbackSheetIn { from { transform:translateY(102%); } }
@keyframes gnFeedbackSkeleton { to { opacity:.45; } }
@media (prefers-reduced-motion: reduce) { .gn-feedback-sheet, .gn-feedback-sheet-backdrop, .gn-feedback-reason-skeleton { animation:none; } .gn-feedback-sheet { transition:opacity 150ms ease; } .gn-feedback-sheet[data-starting-style], .gn-feedback-sheet[data-ending-style] { transform:none; opacity:0; } }
`;

function ensurePopoverStyles() {
  let style = document.getElementById("gn-feedback-popover-style");
  if (style === null) {
    style = document.createElement("style");
    style.id = "gn-feedback-popover-style";
    document.head.append(style);
  }
  style.textContent = FEEDBACK_POPOVER_STYLES;
}

function copyFeedbackTheme(from: Element, to: HTMLElement) {
  const styles = getComputedStyle(from);
  for (const name of POPOVER_VARS) {
    const value = styles.getPropertyValue(name);
    if (value !== "") to.style.setProperty(name, value);
  }
  to.style.font = styles.font;
  to.style.color = styles.color;
}

function FeedbackPopover({
  anchor,
  edge,
  popover,
  children,
}: {
  anchor: HTMLElement | null;
  edge: "start" | "end";
  popover: { current: HTMLDivElement | null };
  children: ComponentChildren;
}) {
  useLayoutEffect(() => {
    const node = popover.current;
    if (anchor === null || node === null) return;
    const apply = () => {
      const anchorRect = anchor.getBoundingClientRect();
      const boundary = feedbackBoundary(anchor);
      node.style.maxWidth = `${String(
        Math.max(0, boundary.right - boundary.left - POPOVER_MARGIN * 2),
      )}px`;
      const placed = placeFeedbackPopover(
        anchorRect,
        node.offsetWidth,
        node.scrollHeight,
        { width: window.innerWidth, height: window.innerHeight },
        edge,
        boundary,
      );
      if (placed === null) {
        delete node.dataset.placed;
        node.style.visibility = "hidden";
        return;
      }
      const next = [
        placed.top,
        placed.bottom,
        placed.left,
        placed.maxHeight,
      ].join("|");
      if (node.dataset.placed === next) return;
      node.dataset.placed = next;
      node.style.top =
        placed.top === null
          ? "auto"
          : `${String(placed.top - anchorRect.top)}px`;
      node.style.bottom =
        placed.bottom === null
          ? "auto"
          : `${String(anchorRect.bottom - window.innerHeight + placed.bottom)}px`;
      node.style.left = `${String(placed.left - anchorRect.left)}px`;
      node.style.right = "auto";
      node.style.maxHeight = `${String(placed.maxHeight)}px`;
      node.style.visibility = "visible";
    };
    apply();
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(apply);
    observer?.observe(node);
    window.addEventListener("resize", apply);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, [anchor, edge]);
  if (anchor === null) return null;
  return createPortal(
    <div class="gn-feedback-popover" ref={popover}>
      {children}
    </div>,
    anchor,
  );
}

function FeedbackSheet({
  anchor,
  dismissible,
  onClose,
  children,
}: {
  anchor: HTMLElement | null;
  dismissible: boolean;
  onClose: () => void;
  children: ComponentChildren;
}) {
  if (anchor === null) return null;
  return (
    <Drawer.Root
      open
      modal
      disablePointerDismissal
      onOpenChange={(nextOpen, details) => {
        if (!nextOpen && !dismissible) {
          details.cancel();
          return;
        }
        if (!nextOpen) onClose();
      }}
    >
      <Drawer.VirtualKeyboardProvider>
        <Drawer.Portal>
          <Drawer.Viewport className="gn-feedback-sheet-viewport">
            <div
              aria-hidden="true"
              class="gn-feedback-sheet-backdrop"
              onClick={() => {
                if (dismissible) onClose();
              }}
            />
            <Drawer.Popup
              ref={(node: HTMLDivElement | null) => {
                if (node === null) return;
                ensurePopoverStyles();
                copyFeedbackTheme(anchor, node);
              }}
              aria-label={m.widget_feedback_label()}
              className="gn-feedback-sheet"
            >
              <div aria-hidden="true" class="gn-feedback-sheet-handle" />
              {children}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.VirtualKeyboardProvider>
    </Drawer.Root>
  );
}

function FeedbackForm({
  reasons,
  reason,
  details,
  loading,
  saving,
  error,
  onReasonChange,
  onDetailsChange,
  onSubmit,
  onCancel,
  onRetry,
}: {
  reasons: FeedbackReason[] | null;
  reason: string;
  details: string;
  loading: boolean;
  saving: boolean;
  error: string;
  onReasonChange: (reason: string) => void;
  onDetailsChange: (details: string) => void;
  onSubmit: (draft: FeedbackDraft) => void;
  onCancel: () => void;
  onRetry: () => void;
}) {
  return (
    <form
      aria-busy={loading || undefined}
      class="gn-feedback-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (reasons === null) return;
        const parsed = reasons.find((item) => item.code === reason);
        if (reasons.length > 0 && !parsed) return;
        onSubmit({
          polarity: "down",
          reason: parsed?.code ?? null,
          details:
            reasons.length === 0 || parsed?.code === "OTHER" ? details : null,
        });
      }}
    >
      <p class="gn-feedback-title">{m.widget_feedback_title()}</p>
      <p class="gn-feedback-help">{m.widget_feedback_help()}</p>
      {loading && (
        <div class="gn-feedback-reasons-loading">
          <span role="status" style={SR_ONLY}>
            {m.widget_feedback_loading()}
          </span>
          {[0, 1, 2].map((item) => (
            <span
              aria-hidden="true"
              class="gn-feedback-reason-skeleton"
              key={item}
            />
          ))}
        </div>
      )}
      {reasons?.map((item) => (
        <label key={item.code} class="gn-feedback-reason">
          <input
            checked={reason === item.code}
            name="reason"
            type="radio"
            value={item.code}
            onChange={() => {
              onReasonChange(item.code);
            }}
          />
          <span>{item.name}</span>
        </label>
      ))}
      {(reasons?.length === 0 || reason === "OTHER") && (
        <textarea
          class="gn-feedback-details"
          maxLength={1000}
          placeholder={m.widget_feedback_details_placeholder()}
          value={details}
          onInput={(event) => {
            onDetailsChange(event.currentTarget.value);
          }}
        />
      )}
      {error !== "" && (
        <div class="gn-feedback-error" role="alert">
          <span>{error}</span>
          {reasons === null && !loading && (
            <button class="gn-feedback-retry" onClick={onRetry} type="button">
              {m.widget_message_retry()}
            </button>
          )}
        </div>
      )}
      <div class="gn-feedback-buttons">
        <button
          class="gn-feedback-send"
          disabled={
            loading ||
            saving ||
            reasons === null ||
            (reasons.length > 0 && reason === "")
          }
          type="submit"
        >
          {m.widget_feedback_send()}
        </button>
        <button
          class="gn-feedback-cancel"
          disabled={saving}
          onClick={onCancel}
          type="button"
        >
          {m.widget_feedback_cancel()}
        </button>
      </div>
    </form>
  );
}

function FeedbackSnapshotView({ snapshot }: { snapshot: FeedbackSnapshot }) {
  return (
    <div class="gn-feedback-note">
      <p class="gn-feedback-title">
        {snapshot.reasonName ?? snapshot.reason ?? m.widget_feedback_default()}
      </p>
      {snapshot.details && <p>{snapshot.details}</p>}
      <p class="gn-feedback-meta">
        {snapshot.memberName
          ? m.widget_feedback_submitted_by({ memberName: snapshot.memberName })
          : m.widget_feedback_submitted()}{" "}
        {new Date(snapshot.submittedAt).toLocaleString()}
      </p>
    </div>
  );
}

/** 每组 AI 回复下方的操作行。反馈表单跟在图标下面，成功之后才改选中态。 */
export function MessageActions({
  text,
  delay,
  edge = "start",
  snapshot,
  onLoadReasons,
  onSubmit,
  onLikeError,
  sentAt,
}: MessageActionsProps) {
  const [copied, copy] = useCopy();
  const mobile = useIsMobile();
  const [tip, setTip] = useState<{ alert: boolean; message: string } | null>(
    null,
  );
  const [burst, setBurst] = useState(0);
  const copiedBefore = useRef(false);
  const root = useRef<HTMLDivElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const downButton = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState<"form" | "snapshot" | null>(null);
  const [reason, setReason] = useState("");
  const [reasons, setReasons] = useState<FeedbackReason[] | null>(null);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadReasons = () => {
    if (!onLoadReasons || loadingReasons) return;
    setLoadingReasons(true);
    setError("");
    void onLoadReasons().then(
      (items) => {
        setReasons(items);
        setLoadingReasons(false);
      },
      () => {
        setError(m.widget_feedback_load_failed());
        setLoadingReasons(false);
      },
    );
  };

  const showTip = (message: string, alert = false) => {
    setTip({ alert, message });
    setBurst((count) => count + 1);
  };
  useEffect(() => {
    if (!copied) return;
    copiedBefore.current = true;
    showTip(m.widget_action_copied());
  }, [copied]);
  useEffect(() => {
    if (tip === null) return;
    const timer = setTimeout(() => {
      setTip(null);
    }, TIP_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [tip, burst]);

  const resetDraft = () => {
    setReason("");
    setDetails("");
    setError("");
  };
  const close = () => {
    setOpen(null);
    resetDraft();
    downButton.current?.focus();
  };
  const dismiss = () => {
    setOpen(null);
  };

  useEffect(() => {
    if (mobile || open !== "form") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointer = (event: PointerEvent) => {
      if (saving) return;
      if (inFeedback(event.composedPath(), root.current, popover.current))
        return;
      dismiss();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer, true);
    };
  }, [mobile, open, saving]);

  useLayoutEffect(() => {
    if (open !== "snapshot") return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
    };
    const onPointer = (event: PointerEvent) => {
      if (inFeedback(event.composedPath(), root.current, popover.current))
        return;
      setOpen(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer, true);
    };
  }, [open]);

  const submit = async (draft: FeedbackDraft) => {
    if (!onSubmit || saving || snapshot) return;
    setSaving(true);
    setError("");
    try {
      await onSubmit(draft);
      setOpen(null);
    } catch (error: unknown) {
      const message =
        error instanceof MerchantFeedbackNetworkError
          ? m.widget_feedback_network_failed()
          : m.widget_feedback_send_failed();
      if (draft.polarity === "up") {
        if (onLikeError) onLikeError(message);
        else showTip(message, true);
      } else setError(message);
      if (draft.polarity === "down" && onLoadReasons) {
        void onLoadReasons().then(setReasons, () => undefined);
      }
    } finally {
      setSaving(false);
    }
  };

  const like = () => {
    if (snapshot) return;
    void submit({ polarity: "up", reason: null, details: null });
  };
  const dislike = () => {
    if (snapshot) {
      if (snapshot.polarity === "down")
        setOpen((current) => (current === "snapshot" ? null : "snapshot"));
      return;
    }
    if (open === "form") {
      close();
      return;
    }
    setOpen("form");
    if (reasons !== null || !onLoadReasons) return;
    loadReasons();
  };

  const feedbackContent =
    open === "form" ? (
      <FeedbackForm
        reasons={reasons}
        reason={reason}
        details={details}
        loading={loadingReasons}
        saving={saving}
        error={error}
        onReasonChange={setReason}
        onDetailsChange={setDetails}
        onSubmit={(draft) => {
          void submit(draft);
        }}
        onCancel={close}
        onRetry={loadReasons}
      />
    ) : open === "snapshot" && snapshot?.polarity === "down" ? (
      <FeedbackSnapshotView snapshot={snapshot} />
    ) : null;

  return (
    <div
      ref={root}
      class="gn-feedback"
      data-edge={edge}
      data-open={open !== null || undefined}
      data-chosen={snapshot !== undefined || undefined}
      style={{ animationDelay: delay }}
    >
      <div class="gn-message-actions gn-feedback-entry">
        {text !== undefined && (
          <button
            aria-live="polite"
            class="gn-action"
            data-copied={copied || undefined}
            onClick={() => {
              void copy(text);
            }}
            title={m.widget_message_copy()}
            type="button"
          >
            {copied ? (
              <span key="copied" class="gn-action-icon gn-action-pop">
                <CheckIcon size={14} strokeWidth={2.6} />
              </span>
            ) : (
              <span
                key="copy"
                class={
                  copiedBefore.current
                    ? "gn-action-icon gn-action-pop-back"
                    : "gn-action-icon"
                }
              >
                <CopyIcon size={14} />
              </span>
            )}
            <span style={SR_ONLY}>
              {copied ? m.widget_action_copied() : m.widget_message_copy()}
            </span>
          </button>
        )}
        {onSubmit && (
          <span class="gn-feedback-votes">
            <button
              aria-label={m.widget_message_like()}
              aria-pressed={snapshot?.polarity === "up"}
              class="gn-action gn-feedback-hit"
              data-vote={snapshot?.polarity === "up" ? "up" : undefined}
              disabled={saving || snapshot !== undefined}
              onClick={like}
              title={m.widget_message_like()}
              type="button"
            >
              <ThumbIcon size={15} filled={snapshot?.polarity === "up"} />
            </button>
            <button
              ref={downButton}
              aria-label={m.widget_message_dislike()}
              aria-pressed={snapshot?.polarity === "down"}
              class="gn-action gn-feedback-hit"
              data-vote={snapshot?.polarity === "down" ? "down" : undefined}
              disabled={saving || snapshot?.polarity === "up"}
              onClick={dislike}
              title={m.widget_message_dislike()}
              type="button"
            >
              <ThumbIcon
                size={15}
                down
                filled={snapshot?.polarity === "down"}
              />
            </button>
          </span>
        )}
        {tip !== null && (
          <div
            key={`tip${String(burst)}`}
            aria-hidden={tip.alert ? undefined : "true"}
            class="gn-action-tip"
            role={tip.alert ? "alert" : undefined}
          >
            {tip.message}
          </div>
        )}
        {sentAt !== undefined && (
          <time class="gn-feedback-time" dateTime={sentAt.value}>
            {sentAt.label}
          </time>
        )}
      </div>
      {feedbackContent !== null &&
        (mobile && open === "form" ? (
          <FeedbackSheet
            anchor={root.current}
            dismissible={!saving}
            onClose={dismiss}
          >
            {feedbackContent}
          </FeedbackSheet>
        ) : (
          <FeedbackPopover anchor={root.current} edge={edge} popover={popover}>
            {feedbackContent}
          </FeedbackPopover>
        ))}
    </div>
  );
}

export const MESSAGE_ACTION_STYLES = `
${FEEDBACK_POPOVER_STYLES}
.gn-chat-panel { --gn-action-accent-tint:10%; --gn-action-success-tint:12%; }
.gn-chat-panel[data-color-scheme="dark"] { --gn-action-accent-tint:18%; --gn-action-success-tint:16%; }
.gn-feedback { --gn-message-bg:var(--bg-surface-raised, var(--gn-message-bg)); --gn-message-line:var(--border-surface-default, var(--gn-message-line)); position:relative; display:flex; flex-direction:column; gap:8px; max-width:100%; padding-top:4px; color:var(--content-fg-strong, inherit); animation:gnMsgIn .24s cubic-bezier(.2,.8,.2,1) both; }
.gn-feedback[data-open] { z-index:30; }
.gn-feedback[data-edge="end"] { align-items:flex-end; }
.gn-feedback[data-edge="start"] { align-items:flex-start; align-self:flex-start; }
.gn-message-actions { position:relative; display:flex; align-items:center; }
.gn-feedback-votes { display:flex; max-inline-size:0; overflow:hidden; opacity:0; transition:max-inline-size .2s ease .25s, opacity .2s ease .25s; }
.gn-feedback-votes .gn-feedback-hit { flex:none; }
[data-slot="message-content"] > :not(.gn-reply-tail):hover ~ .gn-reply-tail .gn-feedback-votes { max-inline-size:54px; opacity:1; transition-delay:1s; }
.gn-feedback-votes:hover, .gn-feedback:focus-within .gn-feedback-votes, .gn-feedback[data-open] .gn-feedback-votes, .gn-feedback[data-chosen] .gn-feedback-votes { max-inline-size:54px; opacity:1; transition-delay:0s; }
.gn-feedback-time { margin-inline-start:6px; color:var(--content-fg-subtle, inherit); font-size:11px; line-height:1; white-space:nowrap; }
.gn-action { position:relative; display:grid; place-items:center; width:24px; height:24px; padding:0; border:0; border-radius:6px; background:transparent; color:var(--gn-action); cursor:pointer; transition:color .2s, background .2s, transform .12s; }
.gn-feedback-hit { width:27px; height:27px; border-radius:2px; color:var(--content-fg-strong, var(--gn-action)); }
.gn-action:hover { background:var(--gn-action-hover); }
.gn-action.gn-feedback-hit:hover { background:transparent; }
.gn-action:active { transform:scale(.9); }
.gn-action:disabled { cursor:default; }
.gn-action[data-copied] { color:var(--gn-action-success); background:color-mix(in srgb, var(--gn-action-success) var(--gn-action-success-tint), transparent); }
.gn-action-icon { display:grid; place-items:center; }
.gn-action-tip { position:absolute; left:0; top:-26px; padding:3px 8px; border-radius:6px; background:var(--gn-tip-bg); color:var(--gn-tip-fg); font-size:11px; line-height:1.4; white-space:nowrap; pointer-events:none; }
@media (max-width:639px), (max-height:579px) {
  .gn-feedback { padding-top:0; }
  .gn-action, .gn-feedback-hit { width:24px; height:24px; }
  .gn-feedback-time { margin-inline-start:4px; }
}
@media (hover:none), (pointer:coarse) {
  .gn-feedback-votes { max-inline-size:54px; opacity:1; transition-delay:0s; }
}
@media (prefers-reduced-motion: no-preference) {
  .gn-action-pop { animation:gnActionPop .3s ease-out; }
  .gn-action-pop-back { animation:gnActionPop .2s ease-out; }
  .gn-action-tip { animation:gnActionTip .18s ease-out; }
}
@keyframes gnActionPop { 0% { transform:scale(.6); opacity:0; } 60% { transform:scale(1.2); } 100% { transform:scale(1); opacity:1; } }
@keyframes gnActionTip { 0% { opacity:0; transform:translateY(4px); } 100% { opacity:1; transform:none; } }
`;
